import { type FC, useState } from "react";

import { ArrowRight, Lock, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { Sheet, SheetContent, SheetTitle } from "@/shared/components/ui/sheet";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { useSaveEIN } from "@/shared/hooks/useSaveEIN";
import { supabase } from "@/shared/lib/supabase";
import { SETTLE_TASK_IDS } from "@/shared/constants/taskIds";

function formatEIN(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}-${digits.slice(2)}`;
}

function isValidEIN(value: string): boolean {
  return value.replace(/\D/g, "").length === 9;
}

interface AlreadyHaveEINDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  estateId: number;
}

export const AlreadyHaveEINDrawer: FC<AlreadyHaveEINDrawerProps> = ({
  isOpen,
  onClose,
  estateId,
}) => {
  const [einValue, setEinValue] = useState("");
  const { saveEIN, isLoading: isSaving } = useSaveEIN();
  const queryClient = useQueryClient();

  const isValid = isValidEIN(einValue);

  const handleEINChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEinValue(formatEIN(e.target.value));
  };

  const handleSubmit = async () => {
    if (!isValid) return;

    try {
      const rawEIN = einValue.replace(/\D/g, "");
      await saveEIN({ ein: rawEIN, estateId });

      const now = new Date().toISOString();
      const { error } = await supabase.from("estate_tasks").upsert(
        {
          estate_id: estateId,
          task_id: SETTLE_TASK_IDS.GET_EIN,
          status: "completed",
          completed_at: now,
          last_active_at: now,
        },
        { onConflict: "estate_id,task_id" },
      );
      if (error) throw error;

      await queryClient.invalidateQueries({ queryKey: ["moduleProgress", estateId] });
      await queryClient.invalidateQueries({ queryKey: ["deceasedEINData", estateId] });

      enqueueSnackbar("EIN saved", { variant: "success" });
      setEinValue("");
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save EIN";
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const handleClose = () => {
    setEinValue("");
    onClose();
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <SheetContent
        side="right"
        showClose={false}
        overlayClassName="bg-black/30 backdrop-blur-[2px]"
        className="w-full sm:max-w-none sm:w-[50vw] p-0 flex flex-col border-l border-primary shadow-[0_0_24px_0_rgba(0,0,0,0.09)] bg-white"
      >
        <div className="flex flex-col gap-3 border-b border-primary p-6 shrink-0">
          <div className="flex items-center gap-3 w-full">
            <span className="text-base font-medium tracking-[-0.02em] text-secondary shrink-0">
              Step 1
            </span>
            <span className="h-1 w-1 rounded-full bg-stone-400 shrink-0" aria-hidden="true" />
            <span className="text-base font-medium tracking-[-0.02em] text-secondary flex-1 min-w-0 truncate">
              Get the estate's tax ID
            </span>
            <button
              onClick={handleClose}
              className="flex h-5 w-5 shrink-0 items-center justify-center hover:opacity-70"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex items-center gap-4 w-full">
            <div className="relative flex-1 h-2 rounded-full bg-background overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-brand transition-all duration-300"
                style={{ width: "100%" }}
              />
            </div>
            <span className="text-sm text-secondary shrink-0 whitespace-nowrap">1 of 1</span>
          </div>
        </div>

        <SheetTitle className="sr-only">Get the estate's tax ID</SheetTitle>

        <div className="flex-1 min-h-0 overflow-y-auto bg-white p-8">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1">
              <h4>Add the estate's tax ID (EIN)</h4>
              <p className="text-sm text-secondary">
                Find it on the IRS letter that confirmed the estate's tax ID, called notice CP 575
              </p>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium leading-[14px]">Tax ID (EIN)</label>
              <Input
                value={einValue}
                onChange={handleEINChange}
                placeholder="XX-XXXXXXX"
              />
            </div>

            <div className="flex items-center gap-2 px-1">
              <Lock className="h-3 w-3 shrink-0 text-tertiary" />
              <p className="text-xs text-tertiary">
                Encrypted and never shared outside this application
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end border-t border-primary p-6 shrink-0">
          <Button
            variant={isValid && !isSaving ? "brand" : "subtle"}
            disabled={!isValid || isSaving}
            onClick={handleSubmit}
          >
            {isSaving ? "Submitting..." : "Submit"}
            <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
